/**
 * Voice-message attachment metadata.
 *
 * The encrypted attachment for a voice message carries a small JSON header
 * alongside the Opus payload so the receiver can render the bubble UI
 * (duration + waveform bar chart) without decoding the audio first. We
 * embed that header *inside* the E2EE plaintext envelope rather than as
 * loose attachment metadata so:
 *
 *   1. The server cannot ship a misleading waveform — every byte of the
 *      VoiceMessageMetadata is covered by the outer envelope's Ed25519
 *      sender signature, same posture as Tombstone / Receipt control
 *      messages.
 *   2. Duration is bound to [200ms, 180_000ms]. A tampered recorder or
 *      hostile peer cannot claim a 24-hour voice memo to game the UI.
 *   3. Sample rate is fixed at 16kHz and bit rate is clamped to
 *      [16kbps, 64kbps]. Together these bound the on-disk size so a
 *      malicious sender cannot push a 50MB "voice message" past us.
 *   4. Waveform is exactly 64 bytes (UInt8 each). The decoder rejects
 *      anything else so a hostile peer cannot smuggle extra bytes
 *      through the metadata field as a covert side-channel.
 *
 * Wire format of the *plaintext that gets encrypted* (NOT the on-the-wire
 * envelope, which is the standard `kaether:e2ee:v1:` form):
 *
 *   kaether:voice:v1:<base64url(JSON)>
 *
 * JSON shape:
 *   { "schema": 1, "durationMillis": 4200, "sampleRateHz": 16000,
 *     "bitRateBps": 32000, "waveformBuckets": "<base64>", "recordedAt": 1700000000 }
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/VoiceMessageMetadata.swift`.
 * Pure-logic only: no MediaRecorder, no AudioContext, no side effects.
 */

// ---------------------------------------------------------------------------
// Value type
// ---------------------------------------------------------------------------

export interface VoiceMessageMetadata {
  readonly schema: number;
  /** Recording length in milliseconds. Clamped to [200, 180_000] on both
   *  encode and decode — see VoiceMessageMetadataCodec. */
  readonly durationMillis: number;
  /** Sample rate of the encoded Opus stream. Voice-message profile is
   *  fixed at 16kHz; anything else is rejected. */
  readonly sampleRateHz: number;
  /** Encoded bit rate in bits per second. Bounded so the receiver can
   *  compute an upper limit on attachment size up front. */
  readonly bitRateBps: number;
  /** Exactly 64 bytes. Each byte is the normalised RMS amplitude
   *  (0–255) of one sub-window of the recording. */
  readonly waveformBuckets: Uint8Array;
  /** Sender-claimed recording timestamp (unix seconds). UI should prefer
   *  the local observedAt for ordering — peer clocks can drift. */
  readonly recordedAt: number;
}

export const VOICE_MESSAGE_SCHEMA_VERSION = 1 as const;

/** Convenience constructor — sets `schema` and `sampleRateHz` to their
 *  required defaults when omitted. */
export function makeVoiceMessageMetadata(opts: {
  durationMillis: number;
  waveformBuckets: Uint8Array;
  sampleRateHz?: number;
  bitRateBps?: number;
  recordedAt?: number;
}): VoiceMessageMetadata {
  return {
    schema: VOICE_MESSAGE_SCHEMA_VERSION,
    durationMillis: opts.durationMillis,
    sampleRateHz: opts.sampleRateHz ?? 16_000,
    bitRateBps: opts.bitRateBps ?? 32_000,
    waveformBuckets: opts.waveformBuckets,
    recordedAt: opts.recordedAt ?? Math.floor(Date.now() / 1000),
  };
}

// ---------------------------------------------------------------------------
// Error types
// ---------------------------------------------------------------------------

export type VoiceMessageMetadataErrorKind =
  | { readonly kind: "malformed"; readonly detail: string }
  | { readonly kind: "unsupportedSchema"; readonly schema: number }
  | { readonly kind: "invalidDuration"; readonly durationMillis: number }
  | { readonly kind: "invalidSampleRate"; readonly sampleRateHz: number }
  | { readonly kind: "invalidBitRate"; readonly bitRateBps: number }
  | { readonly kind: "invalidWaveformLength"; readonly length: number };

export class VoiceMessageMetadataError extends Error {
  readonly cause: VoiceMessageMetadataErrorKind;

  constructor(cause: VoiceMessageMetadataErrorKind) {
    super(VoiceMessageMetadataError.describe(cause));
    this.name = "VoiceMessageMetadataError";
    this.cause = cause;
  }

  private static describe(c: VoiceMessageMetadataErrorKind): string {
    switch (c.kind) {
      case "malformed":
        return `malformed voice message: ${c.detail}`;
      case "unsupportedSchema":
        return `unsupported schema: ${c.schema}`;
      case "invalidDuration":
        return `invalid duration: ${c.durationMillis}ms (must be 200–180_000)`;
      case "invalidSampleRate":
        return `invalid sample rate: ${c.sampleRateHz}Hz (must be 16000)`;
      case "invalidBitRate":
        return `invalid bit rate: ${c.bitRateBps}bps (must be 16_000–64_000)`;
      case "invalidWaveformLength":
        return `invalid waveform length: ${c.length} bytes (must be exactly 64)`;
    }
  }
}

// ---------------------------------------------------------------------------
// Codec
// ---------------------------------------------------------------------------

/** Raw JSON shape stored in the base64url blob. */
interface VoiceMessageMetadataJson {
  schema: number;
  durationMillis: number;
  sampleRateHz: number;
  bitRateBps: number;
  waveformBuckets: string; // standard base64 of 64 bytes
  recordedAt: number;
}

export const VoiceMessageMetadataCodec = {
  PREFIX: "kaether:voice:v1:" as const,
  MAX_DURATION_MILLIS: 180_000,
  MIN_DURATION_MILLIS: 200,
  WAVEFORM_BUCKET_COUNT: 64,
  ALLOWED_SAMPLE_RATES: new Set([16_000]),
  MIN_BIT_RATE: 16_000,
  MAX_BIT_RATE: 64_000,

  /** Returns true when the plaintext string starts with the voice-message prefix. */
  isVoiceMessage(s: string): boolean {
    return s.startsWith(VoiceMessageMetadataCodec.PREFIX);
  },

  /**
   * Encode `m` to the `kaether:voice:v1:<base64url(JSON)>` wire format.
   * Validates on encode — throws `VoiceMessageMetadataError` on invalid input.
   */
  encode(m: VoiceMessageMetadata): string {
    validate(m);
    const json: VoiceMessageMetadataJson = {
      schema: m.schema,
      durationMillis: m.durationMillis,
      sampleRateHz: m.sampleRateHz,
      bitRateBps: m.bitRateBps,
      waveformBuckets: uint8ArrayToBase64(m.waveformBuckets),
      recordedAt: m.recordedAt,
    };
    const jsonString = JSON.stringify(json);
    return (
      VoiceMessageMetadataCodec.PREFIX + base64UrlEncode(stringToUint8Array(jsonString))
    );
  },

  /**
   * Decode a `kaether:voice:v1:<base64url(JSON)>` string.
   * Validates on decode — throws `VoiceMessageMetadataError` on invalid input.
   * Don't trust the wire: both ends enforce the bounds.
   */
  decode(s: string): VoiceMessageMetadata {
    const codec = VoiceMessageMetadataCodec;
    if (!s.startsWith(codec.PREFIX)) {
      throw new VoiceMessageMetadataError({
        kind: "malformed",
        detail: "missing prefix",
      });
    }
    const b64 = s.slice(codec.PREFIX.length);
    let jsonBytes: Uint8Array;
    try {
      jsonBytes = base64UrlDecode(b64);
    } catch {
      throw new VoiceMessageMetadataError({
        kind: "malformed",
        detail: "invalid base64url",
      });
    }

    let parsed: VoiceMessageMetadataJson;
    try {
      parsed = JSON.parse(uint8ArrayToString(jsonBytes)) as VoiceMessageMetadataJson;
    } catch (e) {
      throw new VoiceMessageMetadataError({
        kind: "malformed",
        detail: `json parse failed: ${e instanceof Error ? e.message : String(e)}`,
      });
    }

    let waveformBuckets: Uint8Array;
    try {
      waveformBuckets = base64ToUint8Array(parsed.waveformBuckets);
    } catch {
      throw new VoiceMessageMetadataError({
        kind: "malformed",
        detail: "waveformBuckets is not valid base64",
      });
    }

    const m: VoiceMessageMetadata = {
      schema: parsed.schema,
      durationMillis: parsed.durationMillis,
      sampleRateHz: parsed.sampleRateHz,
      bitRateBps: parsed.bitRateBps,
      waveformBuckets,
      recordedAt: parsed.recordedAt,
    };
    validate(m);
    return m;
  },

  /**
   * Convenience: returns null for non-voice payloads. Throws only for
   * malformed payloads that *did* claim to be voice messages.
   */
  tryDecode(s: string): VoiceMessageMetadata | null {
    if (!VoiceMessageMetadataCodec.isVoiceMessage(s)) return null;
    return VoiceMessageMetadataCodec.decode(s);
  },
} as const;

// ---------------------------------------------------------------------------
// Validation (private)
// ---------------------------------------------------------------------------

function validate(m: VoiceMessageMetadata): void {
  const c = VoiceMessageMetadataCodec;

  if (m.schema !== VOICE_MESSAGE_SCHEMA_VERSION) {
    throw new VoiceMessageMetadataError({
      kind: "unsupportedSchema",
      schema: m.schema,
    });
  }

  if (m.durationMillis < c.MIN_DURATION_MILLIS || m.durationMillis > c.MAX_DURATION_MILLIS) {
    throw new VoiceMessageMetadataError({
      kind: "invalidDuration",
      durationMillis: m.durationMillis,
    });
  }

  if (!c.ALLOWED_SAMPLE_RATES.has(m.sampleRateHz)) {
    throw new VoiceMessageMetadataError({
      kind: "invalidSampleRate",
      sampleRateHz: m.sampleRateHz,
    });
  }

  if (m.bitRateBps < c.MIN_BIT_RATE || m.bitRateBps > c.MAX_BIT_RATE) {
    throw new VoiceMessageMetadataError({
      kind: "invalidBitRate",
      bitRateBps: m.bitRateBps,
    });
  }

  if (m.waveformBuckets.length !== c.WAVEFORM_BUCKET_COUNT) {
    throw new VoiceMessageMetadataError({
      kind: "invalidWaveformLength",
      length: m.waveformBuckets.length,
    });
  }
}

// ---------------------------------------------------------------------------
// base64 / base64url helpers
// ---------------------------------------------------------------------------

function base64UrlEncode(bytes: Uint8Array): string {
  return uint8ArrayToBase64(bytes)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function base64UrlDecode(s: string): Uint8Array {
  let padded = s.replace(/-/g, "+").replace(/_/g, "/");
  const pad = (4 - (padded.length % 4)) % 4;
  padded += "=".repeat(pad);
  return base64ToUint8Array(padded);
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

function base64ToUint8Array(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function stringToUint8Array(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

function uint8ArrayToString(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes);
}

/** Deep equality for VoiceMessageMetadata (Uint8Array comparison). */
export function voiceMessageMetadataEqual(
  a: VoiceMessageMetadata,
  b: VoiceMessageMetadata,
): boolean {
  if (
    a.schema !== b.schema ||
    a.durationMillis !== b.durationMillis ||
    a.sampleRateHz !== b.sampleRateHz ||
    a.bitRateBps !== b.bitRateBps ||
    a.recordedAt !== b.recordedAt
  ) {
    return false;
  }
  if (a.waveformBuckets.length !== b.waveformBuckets.length) return false;
  for (let i = 0; i < a.waveformBuckets.length; i++) {
    if (a.waveformBuckets[i] !== b.waveformBuckets[i]) return false;
  }
  return true;
}
